import { updateWin32StyleReadingPaneViewPreview, type ViewType } from "owa-mail-organization-settings";
import { getUserConfiguration } from "owa-session-store";
import type ReactListViewType from "owa-service/lib/contract/ReactListViewType";
import type { ReadingPaneConversationOptions } from "owa-outlook-service-option-store";
import { getOptionsForFeature, OwsOptionsFeatureType } from "owa-outlook-service-option-store";
import setMailOrganizationViewTypes from "owa-mail-organization-settings/lib/mutators/setMailOrganizationViewTypes";
import { lazyReadOcsSettings } from "owa-ocs-service-gql";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";

const OCS_READING_PANE_VIEW_TYPE_NAME = "mail_readingpaneviewtype";
const OCS_LIST_VIEW_EXPANSION_TYPE_NAME = "mail_listviewexpansiontype";

export default async function initializeMailOrganizationViewTypes(): Promise<void> {
    let [listViewExpansionType, readingPaneViewType] = await getOcsSetting();
    
    if (listViewExpansionType == 3 || readingPaneViewType == 3) {
        const isRPConversationEnabled = getLegacyRPConversationEnabledOption();
        const userOptions = getUserConfiguration().UserOptions;
        
        if (userOptions) {
            switch (userOptions.GlobalListViewTypeReact) {
                case 0:
                    if (isRPConversationEnabled) {
                        // Classic stacked mode
                        listViewExpansionType = 0;
                        readingPaneViewType = 0;
                        break;
                    }
                    else {
                        // Win32 style mode
                        listViewExpansionType = 1;
                        readingPaneViewType = 2;
                        break;
                    }
                case 1:
                    // Classic message mode
                    listViewExpansionType = 2;
                    readingPaneViewType = 2;
                    break;
                default:
                    // Default to classic stacked mode
                    listViewExpansionType = 0;
                    readingPaneViewType = 0;
                    throw new Error("Invalid GlobalListViewTypeReact");
            }
        }
        else {
            // Default to classic stacked mode
            listViewExpansionType = 0;
            readingPaneViewType = 0;
        }
    }
    
    setMailOrganizationViewTypes(listViewExpansionType, readingPaneViewType);
    if (isWin32StylingEnabled()) {
        updateWin32StyleReadingPaneViewPreview(true, readingPaneViewType);
    }
}

// This function is used to get the legacy reading pane conversation option
// OWS' ReadingPaneConversationOptions and GlobalListViewTypeReact
function getLegacyRPConversationEnabledOption(): boolean {
    const readingPaneConversationOptions = getOptionsForFeature<ReadingPaneConversationOptions>(OwsOptionsFeatureType.ReadingPaneConversation);
    if (readingPaneConversationOptions) {
        return readingPaneConversationOptions.conversationEnabled;
    }
    else {
        const userOptions = getUserConfiguration().UserOptions;
        return userOptions?.GlobalListViewTypeReact === 0;
    }
}

async function getOcsSetting(): Promise<[
    ViewType,
    ViewType
]> {
    try {
        let messageListExpansionViewTypeString: undefined | string;
        let readingPaneViewTypeString: undefined | string;
        
        const ocsResponse = await lazyReadOcsSettings.importAndExecute(getGlobalSettingsAccountMailboxInfo(), "GLOBAL", [OCS_LIST_VIEW_EXPANSION_TYPE_NAME, OCS_READING_PANE_VIEW_TYPE_NAME], "getMailOrganizationViewTypes");
        
        if (!ocsResponse || ocsResponse.length == 0) {
            return [3, 3];
        }
        
        for (const ocsEntry of ocsResponse) {
            const entryName = ocsEntry.name?.toLowerCase();
            if (entryName === OCS_LIST_VIEW_EXPANSION_TYPE_NAME) {
                // Will default to empty string if no value is found
                messageListExpansionViewTypeString = ocsEntry.value?.toString();
            }
            
            if (entryName === OCS_READING_PANE_VIEW_TYPE_NAME) {
                // Will default to empty string if no value is found
                readingPaneViewTypeString = ocsEntry.value?.toString();
            }
        }
        
        return [
            convertStringToViewType(messageListExpansionViewTypeString),
            convertStringToViewType(readingPaneViewTypeString),
        ];
    }
    catch {
        // If there is an error try to get from the prime settings
        return getPrimeSettingsViewType();
    }
}

// This function gets the new view types from OCS PrimeBootSettings
function getPrimeSettingsViewType(): [
    ViewType,
    ViewType
] {
    const userConfig = getUserConfiguration();
    const primeSettingsItems = userConfig.PrimeSettings?.Items;
    let messageListExpansionViewTypeString: undefined | string;
    let readingPaneViewTypeString: undefined | string;
    if (primeSettingsItems) {
        // Iterate through the prime boot settings and get view type values
        for (const primeSetting of primeSettingsItems) {
            const primeSettingId = primeSetting.Id?.toLowerCase();
            if (primeSettingId === OCS_LIST_VIEW_EXPANSION_TYPE_NAME) {
                // Will default to empty string if no value is found
                messageListExpansionViewTypeString = primeSetting.Value?.[0]?.value?.toString();
            }
            
            if (primeSettingId === OCS_READING_PANE_VIEW_TYPE_NAME) {
                // Will default to empty string if no value is found
                readingPaneViewTypeString = primeSetting.Value?.[0]?.value?.toString();
            }
            
            // Once both view types are set, break the loop
            if (messageListExpansionViewTypeString != undefined &&
                readingPaneViewTypeString != undefined) {
                break;
            }
        }
    }
    
    return [
        convertStringToViewType(messageListExpansionViewTypeString),
        convertStringToViewType(readingPaneViewTypeString),
    ];
}

function convertStringToViewType(viewTypeString: string | undefined): ViewType {
    if (viewTypeString) {
        switch (viewTypeString) {
            case "0":
                return 0;
            case "1":
                return 1;
            case "2":
                return 2;
        }
    }
    
    return 3;
}
